import { ScramjetClient } from "@client/index";
import { rewriteCss, unrewriteCss } from "@rewriters/css";
import { String } from "@/shared/snapshot";

// A safety net for CSS that reaches the page past the rewriting. The browser loads what a
// style's url() names when it next works out the styles, after the current script and its
// microtasks: a MutationObserver sees the change before that and rewrites it. Found through
// the leak check: React setting `mask: url(...)` on an SVG element's style (Agoda's icons),
// a consent dialog's CSS text (Didomi on Doctolib), and more paths of the same kind. Each of
// them loaded pictures straight from the site, with the visitor's address.
export const order = 10;
export default function (client: ScramjetClient, self: Self) {
	const Observer = (self as any).MutationObserver;
	if (!Observer || !self.document) return;

	const getAttr = (el: Element, name: string) =>
		client.natives.call("Element.prototype.getAttribute", el, name) as string | null;
	const hasAttr = (el: Element, name: string) =>
		client.natives.call("Element.prototype.hasAttribute", el, name) as boolean;
	const setAttr = (el: Element, name: string, value: string) =>
		client.natives.call("Element.prototype.setAttribute", el, name, value);
	const MAY_LOAD = /url\(|@import|image-set\(/i;

	const fixStyleAttr = (el: Element) => {
		const css = getAttr(el, "style");
		if (!css || !MAY_LOAD.test(css)) return;
		const fixed = rewriteCss(css, client.context, client.meta);
		if (fixed === css) return;
		// Reading the attribute back gives the page what it wrote.
		if (hasAttr(el, "scramjet-attr-style")) setAttr(el, "scramjet-attr-style", css);
		setAttr(el, "style", fixed);
	};
	// The text's real content (its data getter below gives the page's own version back).
	const fixText = (text: Text) => {
		const css = client.descriptors.get("CharacterData.prototype.data", text) as string;
		if (!css || !MAY_LOAD.test(css)) return;
		const fixed = rewriteCss(css, client.context, client.meta);
		if (fixed !== css) client.descriptors.set("CharacterData.prototype.data", text, fixed);
	};
	const isStyleElement = (node: any) => node && node.localName === "style";
	const fixStyleElement = (style: Element) => {
		for (const child of style.childNodes as any) {
			if (child.nodeType === 3) fixText(child);
		}
	};
	// An added element and everything in it (built while detached, then inserted).
	const fixSubtree = (root: Element) => {
		const walker = self.document.createTreeWalker(root, 1 /* SHOW_ELEMENT */);
		for (let el: any = root; el; el = walker.nextNode()) {
			if (isStyleElement(el)) fixStyleElement(el);
			else if (el.hasAttribute && hasAttr(el, "style")) fixStyleAttr(el);
		}
	};

	const observer = new Observer((records: MutationRecord[]) => {
		for (const record of records) {
			try {
				if (record.type === "attributes") {
					fixStyleAttr(record.target as Element);
				} else if (record.type === "characterData") {
					if (isStyleElement(record.target.parentNode)) fixText(record.target as Text);
				} else {
					const parentIsStyle = isStyleElement(record.target);
					for (const node of record.addedNodes as any) {
						if (node.nodeType === 3) {
							if (parentIsStyle) fixText(node);
						} else if (node.nodeType === 1) {
							fixSubtree(node);
						}
					}
				}
			} catch {
				// a node that went away in the meantime
			}
		}
	});
	const watch = (root: Node) => {
		try {
			observer.observe(root, {
				subtree: true,
				childList: true,
				characterData: true,
				attributes: true,
				attributeFilter: ["style"],
			});
		} catch {
			// nothing to watch
		}
	};
	watch(self.document);

	// Text changed inside a <style> by hand (text.data = ..., nodeValue, appendData): rewritten
	// at once, not only by the observer, as the page may make the browser work out the styles
	// (by reading a size) before the observer's turn. Read back as the page wrote it.
	const inStyle = (node: any) => {
		try {
			return isStyleElement(node.parentNode);
		} catch {
			return false;
		}
	};
	for (const path of ["CharacterData.prototype.data", "Node.prototype.nodeValue"]) {
		client.Trap(path, {
			set(ctx, value: any) {
				if (value != null && inStyle(ctx.this)) {
					const css = String(value);
					if (MAY_LOAD.test(css)) return ctx.set(rewriteCss(css, client.context, client.meta));
				}

				return ctx.set(value);
			},
			get(ctx) {
				const value = ctx.get();
				if (typeof value !== "string" || !value.includes("url(") || !inStyle(ctx.this))
					return value;

				return unrewriteCss(value, client.context);
			},
		});
	}
	client.Proxy(
		["CharacterData.prototype.appendData", "CharacterData.prototype.insertData", "CharacterData.prototype.replaceData"],
		{
			apply(ctx) {
				if (!inStyle(ctx.this)) return;
				ctx.call();
				const text = ctx.this as Text;
				const raw = client.descriptors.get("CharacterData.prototype.data", text) as string;
				if (raw && MAY_LOAD.test(raw)) {
					const fixed = rewriteCss(raw, client.context, client.meta);
					if (fixed !== raw) client.descriptors.set("CharacterData.prototype.data", text, fixed);
				}
				ctx.return(undefined);
			},
		}
	);

	// Shadow roots are outside the document's observer; each one gets watched too.
	client.Proxy("Element.prototype.attachShadow", {
		apply(ctx) {
			const root = ctx.call();
			if (root) watch(root);
			ctx.return(root);
		},
	});

	// The style of SVG and MathML elements and of CSS rules is a declaration of its own, apart
	// from HTML elements' (css.ts): values set through it are rewritten the same way.
	for (const path of [
		"SVGElement.prototype.style",
		"MathMLElement.prototype.style",
		"CSSStyleRule.prototype.style",
		"CSSKeyframeRule.prototype.style",
		"CSSPageRule.prototype.style",
		"CSSFontFaceRule.prototype.style",
	]) {
		const [ctor] = path.split(".");
		if (!(self as any)[ctor]) continue;
		client.Trap(path, {
			get(ctx) {
				const style = ctx.get() as CSSStyleDeclaration;
				if (!style) return style;

				return new Proxy(style, {
					get(target, prop) {
						const value = Reflect.get(target, prop);
						if (typeof value === "function") {
							return new Proxy(value, {
								apply(fn, _that, args) {
									return Reflect.apply(fn, style, args);
								},
							});
						}

						return value;
					},
					set(target, prop, value) {
						if (prop === "cssText" || typeof value !== "string" || !MAY_LOAD.test(value)) {
							return Reflect.set(target, prop, value);
						}

						return Reflect.set(
							target,
							prop,
							rewriteCss(String(value), client.context, client.meta)
						);
					},
				});
			},
		});
	}
}
